/**
 * Explicit path overrides for operations whose endpoint must DIFFER from their operation name.
 *
 * By default every named operation is routed to `${baseUrl}/${operationName}` so Envoy/OTel can
 * attribute errors per operation (GROW-3158). These entries override that default for the sensitive
 * auth/payment operations, which are served from dedicated security-scoped paths (PR #11466). The
 * value is the path suffix appended to the resolved GraphQL base URL. Keyed by GraphQL operation name.
 */
const OPERATION_ENDPOINTS: Record<string, string> = {
  register: '/auth/register',
  paymentsCreateSetupIntent: '/auth/create-trial',
  paymentsSubmitSubscriptionCheckout: '/auth/submit-subscription-checkout',
  paymentsExtendSubscription: '/auth/extend-subscription',
}

/**
 * Operations served by the content API, which does not understand the per-operation path suffix and
 * mis-routes anything below the bare `/query` endpoint — the reason GROW-3158 was rolled back the
 * first time (it broke the SSR category pages). They keep hitting `baseUrl` until the content API
 * serves `/query/<operationName>` like the rest of the gateway.
 *
 * Every content resolver but two is exposed under a `content*` / `marketplace*` operation name, so
 * matching on the prefix keeps new operations in the group excluded by default.
 */
const BARE_ENDPOINT_OPERATION_PREFIXES = ['content', 'marketplace']

/**
 * Named operations that must use the bare endpoint. The first three are unprefixed content API
 * operations; the profile mutations are legacy resolvers that are not served from their derived
 * `/query/<operationName>` routes.
 */
const BARE_ENDPOINT_OPERATIONS = new Set([
  'studioProjectDownloadAssets',
  'GetCategoryProducts',
  'GetFreebiesPageData',
  'editUserProfile',
  'uploadUserAvatar',
  'deleteUserAvatar',
])

function usesBareEndpoint(operationName: string): boolean {
  return (
    BARE_ENDPOINT_OPERATIONS.has(operationName) ||
    BARE_ENDPOINT_OPERATION_PREFIXES.some(prefix => operationName.startsWith(prefix))
  )
}

// The generated documents are stable, module-level template strings, so caching the parsed
// operation name by document avoids re-running the regex on every request.
const operationNameCache = new Map<string, string | null>()

export function getOperationName(query: string): string | null {
  const cached = operationNameCache.get(query)
  if (cached !== undefined) {
    return cached
  }

  const name = query.match(/\b(?:query|mutation|subscription)\s+([A-Za-z0-9_]+)/)?.[1] ?? null
  operationNameCache.set(query, name)
  return name
}

/**
 * Resolve the request URL for a given operation. Operations listed in `OPERATION_ENDPOINTS` use their
 * explicit override path; bare-endpoint operations and anonymous/unparseable queries fall back to
 * `baseUrl`; any other named operation is routed to `${baseUrl}/${operationName}`.
 */
export function resolveOperationUrl(query: string, baseUrl: string): string {
  const operationName = getOperationName(query)
  if (!operationName) {
    return baseUrl
  }

  const override = OPERATION_ENDPOINTS[operationName]
  if (override) {
    return `${baseUrl}${override}`
  }

  if (usesBareEndpoint(operationName)) {
    return baseUrl
  }

  return `${baseUrl}/${operationName}`
}

interface UploadUserAvatarVariables {
  input: {
    file: Blob
  }
}

function isUploadUserAvatarRequest(
  query: string,
  variables: unknown,
): variables is UploadUserAvatarVariables {
  if (getOperationName(query) !== 'uploadUserAvatar' || typeof Blob === 'undefined') {
    return false
  }

  if (typeof variables !== 'object' || variables === null || !('input' in variables)) {
    return false
  }

  const input = variables.input
  return (
    typeof input === 'object' && input !== null && 'file' in input && input.file instanceof Blob
  )
}

function serializeUploadUserAvatarRequest(query: string, variables: UploadUserAvatarVariables) {
  const body = new FormData()
  body.append('operations', JSON.stringify({ query, variables: { input: { file: null } } }))
  body.append('map', JSON.stringify({ 0: ['variables.input.file'] }))
  body.append('0', variables.input.file)
  return body
}

/** Serialize the avatar upload as multipart and keep every other GraphQL request as JSON. */
export function serializeGraphQLRequest(query: string, variables?: unknown) {
  if (!isUploadUserAvatarRequest(query, variables)) {
    return {
      body: JSON.stringify({ query, variables }),
      isMultipart: false as const,
    }
  }

  return { body: serializeUploadUserAvatarRequest(query, variables), isMultipart: true as const }
}

export function getCookie(cname: string) {
  const name = cname + '='
  const decodedCookie = decodeURIComponent(document.cookie)
  const ca = decodedCookie.split(';')

  // oxlint-disable-next-line typescript/prefer-for-of
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') {
      c = c.substring(1)
    }

    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length)
    }
  }

  return ''
}

export const TOKEN_LOCAL_STORAGE_KEY = 'cfToken'

export const AUTH_USER_ID_COOKIE_NAME = 'cfauth_uid'
